import React, { useMemo } from 'react';
import { Text, Group } from 'react-konva';

import FlashbackLayout from 'common/components/RewindModal/Konvas/FlashbackLayout';
import KonvaImage from 'common/components/RewindModal/Konvas/KonvaImage';
import ShareableStage from 'common/components/RewindModal/Konvas/ShareableStage';
import {
  LAYER_WIDTH,
  BLOCK_MARGIN,
  FLASHBACK_COVER_LOGO_TOP_MARGIN,
  FLASHBACK_COVER_LOGO_HEIGHT,
  DEFAULT_FLASHBACK_LAYOUT_PROPS,
  FLASHBACK_TEXT_COLOR,
  FLASHBACK_FONT_FAMILY,
  FLASHBACK_PODIUM_TITLE_FONT_SIZE,
  FLASHBACK_PODIUM_TITLE_FONT_WEIGHT,
  FLASHBACK_PODIUM_TITLE_LINE_HEIGHT,
  FLASHBACK_PODIUM_SUBTITLE_FONT_SIZE,
  FLASHBACK_PODIUM_SUBTITLE_FONT_WEIGHT,
  FLASHBACK_PODIUM_SUBTITLE_LINE_HEIGHT,
  FLASHBACK_PODIUM_TITLE_TO_SUBTITLE_SPACING,
  FLASHBACK_PODIUM_LOGO_TO_TITLE_SPACING,
  FLASHBACK_PODIUM_CARD_1_WIDTH,
  FLASHBACK_PODIUM_CARD_1_HEIGHT,
  FLASHBACK_PODIUM_CARD_2_3_WIDTH,
  FLASHBACK_PODIUM_CARD_2_3_HEIGHT,
  FLASHBACK_PODIUM_CARD_CORNER_RADIUS,
  FLASHBACK_PODIUM_CARD_2_TRANSLATE_X,
  FLASHBACK_PODIUM_CARD_2_ROTATION,
  FLASHBACK_PODIUM_CARD_3_TRANSLATE_X,
  FLASHBACK_PODIUM_CARD_3_ROTATION,
  FLASHBACK_PODIUM_BADGE_WIDTH,
  FLASHBACK_PODIUM_BADGE_HEIGHT,
  FLASHBACK_PODIUM_BADGE_1_OFFSET_X,
  FLASHBACK_PODIUM_BADGE_1_OFFSET_Y,
  FLASHBACK_PODIUM_BADGE_2_OFFSET_X,
  FLASHBACK_PODIUM_BADGE_2_OFFSET_Y,
  FLASHBACK_PODIUM_BADGE_3_OFFSET_X,
  FLASHBACK_PODIUM_BADGE_3_OFFSET_Y,
  FLASHBACK_PODIUM_RANK_FONT_SIZE,
  FLASHBACK_PODIUM_RANK_FONT_WEIGHT,
  FLASHBACK_PODIUM_RANK_COLOR
} from 'common/components/RewindModal/Konvas/constants';
import { getDPRSize } from 'common/components/RewindModal/helper';
import {
  IMG_BACKGROUND_MOVIE_SHARE,
  IMG_BACKGROUND_SERIES_SHARE,
  IMG_TOP_RANK
} from 'common/configuration/constants';

type PodiumItem = {
  id: string;
  position: number;
  poster?: { path: string };
};

type ScreenPodiumProps = {
  data: {
    title?: string;
    subtitle?: string;
  };
  items: PodiumItem[];
  isSeries?: boolean;
};

const ShareableScreenPodium = React.forwardRef<any, ScreenPodiumProps>(
  ({ data, items, isSeries = false }, ref) => {
    const title = data?.title ?? 'Votre Podium';
    const subtitle = data?.subtitle;
    const topItems = items.slice(0, 3);

    const backgroundImage = isSeries
      ? IMG_BACKGROUND_SERIES_SHARE
      : IMG_BACKGROUND_MOVIE_SHARE;

    const layout = useMemo(() => {
      const titleHeight =
        FLASHBACK_PODIUM_TITLE_FONT_SIZE * FLASHBACK_PODIUM_TITLE_LINE_HEIGHT;
      const subtitleHeight = subtitle
        ? FLASHBACK_PODIUM_SUBTITLE_FONT_SIZE *
          FLASHBACK_PODIUM_SUBTITLE_LINE_HEIGHT
        : 0;
      const titleToSubtitleSpacing = subtitle
        ? FLASHBACK_PODIUM_TITLE_TO_SUBTITLE_SPACING
        : 0;

      const topMargin =
        FLASHBACK_COVER_LOGO_TOP_MARGIN +
        FLASHBACK_COVER_LOGO_HEIGHT +
        FLASHBACK_PODIUM_LOGO_TO_TITLE_SPACING;

      const isTop3Allocine =
        title === 'Top 3 séries Allociné' || title === 'Top 3 films Allociné';
      const titleToCardsSpacing = isTop3Allocine
        ? getDPRSize(150)
        : getDPRSize(100);

      const y_title = 0;
      const y_subtitle = y_title + titleHeight + titleToSubtitleSpacing;
      const card1Y =
        y_title +
        titleHeight +
        titleToSubtitleSpacing +
        subtitleHeight +
        titleToCardsSpacing;

      const card1Bottom = card1Y + FLASHBACK_PODIUM_CARD_1_HEIGHT;
      const card2_3Overlap = FLASHBACK_PODIUM_CARD_2_3_HEIGHT / 2;
      const card2_3Y = card1Bottom - card2_3Overlap;

      return {
        topMargin,
        y_title,
        y_subtitle,
        card1Y,
        card2_3Y
      };
    }, [subtitle, title]);

    const centerX = LAYER_WIDTH / 2;

    return (
      <ShareableStage ref={ref}>
        <FlashbackLayout
          {...DEFAULT_FLASHBACK_LAYOUT_PROPS}
          backgroundImage={backgroundImage}
        >
          <Group y={layout.topMargin}>
            <Text
              text={title}
              fontSize={FLASHBACK_PODIUM_TITLE_FONT_SIZE}
              fontFamily={FLASHBACK_FONT_FAMILY}
              fontStyle={FLASHBACK_PODIUM_TITLE_FONT_WEIGHT}
              lineHeight={FLASHBACK_PODIUM_TITLE_LINE_HEIGHT}
              fill={FLASHBACK_TEXT_COLOR}
              width={LAYER_WIDTH - BLOCK_MARGIN * 4}
              x={BLOCK_MARGIN * 2}
              align="center"
              y={layout.y_title}
            />

            {subtitle && (
              <Text
                text={subtitle}
                fontSize={FLASHBACK_PODIUM_SUBTITLE_FONT_SIZE}
                fontFamily={FLASHBACK_FONT_FAMILY}
                fontStyle={FLASHBACK_PODIUM_SUBTITLE_FONT_WEIGHT}
                lineHeight={FLASHBACK_PODIUM_SUBTITLE_LINE_HEIGHT}
                fill={FLASHBACK_TEXT_COLOR}
                width={LAYER_WIDTH - BLOCK_MARGIN * 4}
                x={BLOCK_MARGIN * 2}
                align="center"
                y={layout.y_subtitle}
              />
            )}

            <Group>
              {topItems.map((item, index) => {
                const isCard1 = index === 0;
                const isCard2 = index === 1;
                const isCard3 = index === 2;

                const cardWidth = isCard1
                  ? FLASHBACK_PODIUM_CARD_1_WIDTH
                  : FLASHBACK_PODIUM_CARD_2_3_WIDTH;
                const cardHeight = isCard1
                  ? FLASHBACK_PODIUM_CARD_1_HEIGHT
                  : FLASHBACK_PODIUM_CARD_2_3_HEIGHT;

                const badgeWidth = FLASHBACK_PODIUM_BADGE_WIDTH;
                const badgeHeight = FLASHBACK_PODIUM_BADGE_HEIGHT;

                let cardY = layout.card1Y;
                let rotation = 0;
                let translateX = 0;

                if (isCard2) {
                  translateX = FLASHBACK_PODIUM_CARD_2_TRANSLATE_X;
                  cardY = layout.card2_3Y;
                  rotation = FLASHBACK_PODIUM_CARD_2_ROTATION;
                } else if (isCard3) {
                  translateX = FLASHBACK_PODIUM_CARD_3_TRANSLATE_X;
                  cardY = layout.card2_3Y - getDPRSize(15);
                  rotation = FLASHBACK_PODIUM_CARD_3_ROTATION;
                }

                const cardCenterX = centerX + translateX;
                const cardCenterY = cardY + cardHeight / 2;
                let badgeX = centerX;
                let badgeY = cardY + cardHeight / 2;

                if (isCard1) {
                  badgeX =
                    cardCenterX +
                    cardWidth / 2 +
                    FLASHBACK_PODIUM_BADGE_1_OFFSET_X;
                  badgeY =
                    cardCenterY -
                    cardHeight / 2 +
                    FLASHBACK_PODIUM_BADGE_1_OFFSET_Y;
                } else if (isCard2) {
                  badgeX =
                    cardCenterX -
                    cardWidth / 2 +
                    FLASHBACK_PODIUM_BADGE_2_OFFSET_X;
                  badgeY =
                    cardCenterY -
                    cardHeight / 2 +
                    FLASHBACK_PODIUM_BADGE_2_OFFSET_Y;
                } else if (isCard3) {
                  badgeX =
                    cardCenterX +
                    cardWidth / 2 +
                    FLASHBACK_PODIUM_BADGE_3_OFFSET_X;
                  badgeY =
                    cardCenterY +
                    cardHeight / 2 -
                    FLASHBACK_PODIUM_BADGE_3_OFFSET_Y;
                }

                return (
                  <Group key={item.id}>
                    <Group
                      x={cardCenterX}
                      y={cardCenterY}
                      rotation={rotation}
                      offsetX={cardWidth / 2}
                      offsetY={cardHeight / 2}
                    >
                      <KonvaImage
                        path={item.poster?.path}
                        x={0}
                        y={0}
                        width={cardWidth}
                        height={cardHeight}
                        cornerRadius={FLASHBACK_PODIUM_CARD_CORNER_RADIUS}
                      />
                    </Group>

                    <Group
                      x={badgeX}
                      y={badgeY}
                      offsetX={badgeWidth / 2}
                      offsetY={badgeHeight / 2}
                    >
                      <KonvaImage
                        path={IMG_TOP_RANK}
                        isLocal={true}
                        x={-badgeWidth / 2}
                        y={-badgeHeight / 2}
                        width={badgeWidth}
                        height={badgeHeight}
                      />
                      <Text
                        text={String(item.position)}
                        fontSize={FLASHBACK_PODIUM_RANK_FONT_SIZE}
                        fontFamily={FLASHBACK_FONT_FAMILY}
                        fontStyle={FLASHBACK_PODIUM_RANK_FONT_WEIGHT}
                        fill={FLASHBACK_PODIUM_RANK_COLOR}
                        align="center"
                        verticalAlign="middle"
                        x={-badgeWidth / 2}
                        y={-badgeHeight / 2}
                        width={badgeWidth}
                        height={badgeHeight}
                      />
                    </Group>
                  </Group>
                );
              })}
            </Group>
          </Group>
        </FlashbackLayout>
      </ShareableStage>
    );
  }
);

ShareableScreenPodium.displayName = 'ShareableScreenPodium';
export default ShareableScreenPodium;
